import * as amplitude from '@amplitude/analytics-browser';
import { ANALYTICS_CONFIG } from '@constants';
import { STORAGE_KEYS } from '@constants';

/**
 * Analytics Utility
 * Centralized analytics tracking using Amplitude
 *
 * Usage:
 * - Track events: analytics.track('event_name', { property: 'value' })
 * - Set user: analytics.setUser(userId, { email: 'user@example.com' })
 * - Page views: analytics.trackPageView('/dashboard')
 */

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.isEnabled = ANALYTICS_CONFIG.ENABLED;
  }

  /**
   * Initialize Amplitude Analytics
   * Should be called once when app starts
   */
  init() {
    if (!this.isEnabled) {
      console.log('[Analytics] Analytics is disabled');
      return;
    }

    try {
      amplitude.init(ANALYTICS_CONFIG.AMPLITUDE_API_KEY, {
        defaultTracking: {
          pageViews: false, // We'll track page views manually
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
        },
        autocapture: {
          elementInteractions: true, // Track clicks, form submissions, etc.
        },
      });

      this.isInitialized = true;
      // console.log('[Analytics] Amplitude initialized successfully');

      // Set user if already logged in
      this.setUserFromStorage();
    } catch (error) {
      console.error('[Analytics] Failed to initialize Amplitude:', error);
    }
  }

  /**
   * Set user from localStorage if available
   */
  setUserFromStorage() {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (userData) {
        const user = JSON.parse(userData);
        this.setUser(user.id, {
          email: user.email,
          name: user.name,
        });
      }
    } catch (error) {
      console.error('[Analytics] Failed to set user from storage:', error);
    }
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {Object} eventProperties - Additional properties for the event
   */
  track(eventName, eventProperties = {}) {
    if (!this.isEnabled || !this.isInitialized) {
      if (import.meta.env.DEV) {
        console.log(
          `[Analytics] Event (not sent): ${eventName}`,
          eventProperties
        );
      }
      return;
    }

    try {
      amplitude.track(eventName, {
        ...eventProperties,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error(`[Analytics] Failed to track event ${eventName}:`, error);
    }
  }

  /**
   * Track page view
   * @param {string} pageName - Name of the page
   * @param {Object} pageProperties - Additional properties
   */
  trackPageView(pageName, pageProperties = {}) {
    this.track('Page Viewed', {
      page_name: pageName,
      page_url: window.location.pathname,
      page_title: document.title,
      ...pageProperties,
    });
  }

  /**
   * Set user identity
   * @param {string} userId - User ID
   * @param {Object} userProperties - User properties (email, name, etc.)
   */
  setUser(userId, userProperties = {}) {
    if (!this.isEnabled || !this.isInitialized) {
      return;
    }

    try {
      amplitude.setUserId(userId);
      if (Object.keys(userProperties).length > 0) {
        amplitude.setUserProperties(userProperties);
      }
    } catch (error) {
      console.error('[Analytics] Failed to set user:', error);
    }
  }

  /**
   * Clear user identity (on logout)
   */
  clearUser() {
    if (!this.isEnabled || !this.isInitialized) {
      return;
    }

    try {
      amplitude.setUserId(null);
      amplitude.clearUserProperties();
    } catch (error) {
      console.error('[Analytics] Failed to clear user:', error);
    }
  }

  /**
   * Set user properties
   * @param {Object} properties - User properties to set
   */
  setUserProperties(properties) {
    if (!this.isEnabled || !this.isInitialized) {
      return;
    }

    try {
      amplitude.setUserProperties(properties);
    } catch (error) {
      console.error('[Analytics] Failed to set user properties:', error);
    }
  }

  /**
   * Identify user with additional properties
   * @param {Object} identify - Identify object with properties
   */
  identify(identify) {
    if (!this.isEnabled || !this.isInitialized) {
      return;
    }

    try {
      amplitude.identify(identify);
    } catch (error) {
      console.error('[Analytics] Failed to identify user:', error);
    }
  }

  /**
   * Track button click
   * @param {string} buttonName - Name of the button
   * @param {Object} properties - Additional properties
   */
  trackButtonClick(buttonName, properties = {}) {
    this.track('Button Clicked', {
      button_name: buttonName,
      ...properties,
    });
  }

  /**
   * Track form submission
   * @param {string} formName - Name of the form
   * @param {Object} properties - Additional properties
   */
  trackFormSubmit(formName, properties = {}) {
    this.track('Form Submitted', {
      form_name: formName,
      ...properties,
    });
  }

  /**
   * Track form field interaction
   * @param {string} formName - Name of the form
   * @param {string} fieldName - Name of the field
   * @param {string} action - Action type (focus, blur, change)
   */
  trackFormField(formName, fieldName, action) {
    this.track('Form Field Interaction', {
      form_name: formName,
      field_name: fieldName,
      action: action,
    });
  }

  /**
   * Track navigation
   * @param {string} from - Source page/route
   * @param {string} to - Destination page/route
   */
  trackNavigation(from, to) {
    this.track('Navigation', {
      from: from,
      to: to,
    });
  }

  /**
   * Track search
   * @param {string} searchTerm - Search query
   * @param {Object} properties - Additional properties
   */
  trackSearch(searchTerm, properties = {}) {
    this.track('Search', {
      search_term: searchTerm,
      ...properties,
    });
  }

  /**
   * Track error
   * @param {string} errorType - Type of error
   * @param {string} errorMessage - Error message
   * @param {Object} properties - Additional properties
   */
  trackError(errorType, errorMessage, properties = {}) {
    this.track('Error Occurred', {
      error_type: errorType,
      error_message: errorMessage,
      ...properties,
    });
  }

  /**
   * Track feature usage
   * @param {string} featureName - Name of the feature
   * @param {Object} properties - Additional properties
   */
  trackFeatureUsage(featureName, properties = {}) {
    this.track('Feature Used', {
      feature_name: featureName,
      ...properties,
    });
  }

  /**
   * Track theme change
   * @param {string} theme - Theme name (light/dark)
   */
  trackThemeChange(theme) {
    this.track('Theme Changed', {
      theme: theme,
    });
  }
}

// Export singleton instance
const analytics = new AnalyticsService();

export default analytics;
